import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';

/**
 * The hubs' "Show more" button (useLoadMore). A failed fetch shows a one-line
 * error above it; pressing again retries.
 */
export function LoadMoreButton({
  onPress,
  loading,
  error,
}: {
  onPress: () => void;
  loading: boolean;
  error: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <View style={styles.root}>
      {error ? (
        <Text style={[styles.error, { color: theme.destructive }]}>{t('common.loadMoreError')}</Text>
      ) : null}
      <Pressable
        accessibilityRole="button"
        aria-busy={loading}
        disabled={loading}
        onPress={onPress}>
        {({ pressed }) => (
          <View
            style={[
              styles.button,
              { backgroundColor: theme.card, borderColor: theme.border },
              pressed && { opacity: 0.85 },
            ]}>
            {loading ? <ActivityIndicator size="small" color={theme.mutedForeground} /> : null}
            <Text style={[styles.label, { color: theme.text }]}>
              {loading ? t('common.loadingMore') : t('common.showMore')}
            </Text>
          </View>
        )}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    alignItems: 'center',
    gap: Spacing.two,
    paddingVertical: Spacing.three,
  },
  error: {
    fontSize: 14,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
    minWidth: 200,
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.two + 2,
    borderWidth: 1,
    borderRadius: 999,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
  },
});
